import { isBoolean } from "@ciaopeople/cp-shatsu/TypeChecking";
import { AdUnit } from "../AdUnit.js";
import { GDPRSdk } from "../GDPR.js";
import { AdCallParams } from "../AdCallParams.js";
import { AdParamsEnricher, PrebidModule } from "./PrebidModule.js";
import { UserAdsData } from "../adcall/composable/UserAdsData.js";
import { AmazonHBBaseImpl, APSTagSlot, CustomWindow, DefaultVideoBidSlot } from "@ciaopeople/cp-shatsu/Ads";
import { VideoInfoRawData } from "../../../model/video/VideoInfo.js";
import { CP_EmbedType } from "../adcall/CustomParams.js";



const AMZ_NO_BID = "nobid";




function fixStringForPreSerialized(bid_string: string): string {
    let decoded = decodeURIComponent(bid_string);
    if (decoded.substring(0, 1) == "&") decoded = decoded.substring(1);
    return encodeURIComponent(decoded);

}

/** start - da esportare da shatsu */

const INVALID_BID = "_fp_invalid";

interface ExternalBidVariables {

    _fp_amz_scp?: string,
    _fp_amz_enabled: boolean,
    _fp_amz_page_load?: boolean
}


function isExternalBidVariables(x: any): x is ExternalBidVariables {
    return (("_fp_amz_enabled" in x) && isBoolean(x._fp_amz_enabled)) &&
        x._fp_amz_enabled;
}

/** end */

function getExternalBid(w: Window, timeout: number): Promise<string> {
    if (isExternalBidVariables(w)) {
        const cleanAndGet = () => {
            const flag = w._fp_amz_scp;
            w._fp_amz_scp = null;
            w._fp_amz_page_load = false;
            return flag;
        }
        if (w._fp_amz_page_load) {
            const startTime = new Date().getTime();
            return new Promise((resolve, reject) => {

                const rec = () => {

                    if (w._fp_amz_scp && w._fp_amz_scp.trim().length > 0) {
                        if (w._fp_amz_scp != INVALID_BID) resolve(cleanAndGet());
                        else reject("invalid external bid");
                    }
                    else {
                        if (new Date().getTime() - startTime >= timeout) {
                            return reject("external bid search timeout");
                        }
                        else w.setTimeout(rec, 50);
                    }
                }
                rec();
            })
        }
        else {
            if (w._fp_amz_scp && w._fp_amz_scp.trim().length > 0 && w._fp_amz_scp != INVALID_BID) {
                return Promise.resolve(cleanAndGet());
            }
            else {
                return null;
            }
        }
    }
    else return null;
}



export enum AmazonBidBehaviour {


    CHECK_FOR_EXPORTED_BIDS = "check_for_exported",
    ALLOW_PRELOADING_NEXT_BID = "allow_preloading",
    ONLY_ON_DEMAND = "only_on_demand"


}


export class AmazonBidder implements PrebidModule {


    private bidImpl: AmazonHBBaseImpl


    private preloadedBid: Promise<string>;



    constructor(private timeout: number,
        private publisherId: string,
        private userData: UserAdsData,
        private window: Window,
        private playerType: CP_EmbedType,
        private bidBehaviour: AmazonBidBehaviour,
        private gdpr: Promise<GDPRSdk>) {

        this.bidImpl = new AmazonHBBaseImpl(window as CustomWindow, gdpr, userData, {
            pubID: this.publisherId,
            adServer: "googletag",
            videoAdServer: "DFP",
            bidTimeout: this.timeout,
            deals: true
        }, false)


    }


    load(): Promise<void> {
        return this.bidImpl.loadSdk().then(_ => {
            return
        })
    }


    bidAndGetEnricher(adUnit: AdUnit, info: VideoInfoRawData): Promise<AdParamsEnricher> {

        let bidPromise: Promise<string>;

        if (this.bidBehaviour == AmazonBidBehaviour.CHECK_FOR_EXPORTED_BIDS) {
            console.info("HB-" + this.providerName, "Checking exported bids");
            bidPromise = getExternalBid(this.window, this.timeout);
        }
        else if (this.bidBehaviour == AmazonBidBehaviour.ALLOW_PRELOADING_NEXT_BID) {
            console.info("HB-" + this.providerName, "consuming preloaded bid");
            bidPromise = this.consumePreloadedBid();
        }

        if (!bidPromise) bidPromise = this.fetchBidPromise(adUnit);

        return bidPromise.then(bid => {
            return (params: AdCallParams) => {
                if (!bid) {
                    bid = `amznbid=${AMZ_NO_BID}`;
                }
                const useParams: AdCallParams = params ? params : {};
                useParams.custom_params = useParams.custom_params ? useParams.custom_params : {};
                useParams.pre_serialized_custom_params = useParams.pre_serialized_custom_params ? useParams.pre_serialized_custom_params : [];
                const fixed = fixStringForPreSerialized(bid.trim());
                useParams.pre_serialized_custom_params.push(fixed);
                console.log("[CUST_PARAMS] Appended amazon to pre_serialized", useParams);
                return useParams;
            }
        }).catch(e => {
            console.log("HB-" + this.providerName, "missing bid passing nobid", e);
            return this.fallbackAdParamsEnricher
        });
    }



    public get providerName(): string {
        return "A9";
    }



    public preloadNextBid(): void {
        if (this.bidBehaviour == AmazonBidBehaviour.ALLOW_PRELOADING_NEXT_BID) {
            if (!this.preloadedBid) {
                console.info("HB-" + this.providerName, "preloading next bid");
                this.preloadedBid = this.load().then(_ => this.fetchBidPromise());
            }
        }
        else {
            console.info("HB-" + this.providerName, "preloading not allowed - discarding request");
        }
    }


    private consumePreloadedBid(): Promise<string> {
        const x = this.preloadedBid;
        this.preloadedBid = null;
        return x;
    }



    private fetchBidPromise(adUnit?: AdUnit): Promise<string> {
        return new Promise((resolve, reject) => {




            const slotVideo: APSTagSlot = adUnit ? {
                ...DefaultVideoBidSlot,
                videoSignals: {
                    plcmt: (this.playerType == CP_EmbedType.VIDEO_TESTO || this.playerType == CP_EmbedType.VIDEO || this.playerType == CP_EmbedType.EMBED) ? 1 : 2,
                    pos: 1,
                    startdelay: adUnit.label == "preroll" ? 0 : adUnit.label == "postroll" ? -2 : -1,
                    h: 480,
                    w: 640
                }
            } : DefaultVideoBidSlot;



            this.bidImpl.fetchBids([slotVideo])
                .then((bids) => {
                    let encodedQS = null;
                    if (bids) {
                        const bid = bids.find(bid => {
                            return bid.mediaType == "video"
                        });
                        if (bid) {
                            if (bid.encodedQsParams) {
                                encodedQS = bid.encodedQsParams;
                            }
                            else if (bid.helpers?.encodedQsParams) {
                                encodedQS = bid.helpers.encodedQsParams();
                            }
                        }

                    }

                    if (encodedQS) resolve(encodedQS);
                    else {
                        console.info("HB-" + this.providerName, "no response on fetched bid");
                        resolve(null);
                    }
                })
        })
    }

    private fallbackAdParamsEnricher: AdParamsEnricher = (params: AdCallParams) => {
        const bid = `amznbid=${AMZ_NO_BID}`;
        const useParams: AdCallParams = params ? params : {};
        useParams.custom_params = useParams.custom_params ? useParams.custom_params : {};
        useParams.pre_serialized_custom_params = useParams.pre_serialized_custom_params ? useParams.pre_serialized_custom_params : [];
        const fixed = fixStringForPreSerialized(bid.trim());
        useParams.pre_serialized_custom_params.push(fixed);
        return useParams;
    }




}
